import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/http';
import { api, useActs, useCheckoutStatus, useInvoice, useClientRole } from '@/api';
import { clientCan } from '@/lib/access';
import { hasActs, providerOf, useContour } from '@/lib/contour';
import { PayDialog } from '@/components/PayDialog';
import { Meta, Status } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { formatDate, formatDue, formatMoney, formatQuantity } from '@/lib/format';
import { actStatus, invoiceStatus, methodLabel } from '@/lib/status';

export function InvoiceDetail() {
  const { invoiceId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const invQ = useInvoice(invoiceId);
  const inv = invQ.data;
  const { data: acts } = useActs();
  const contour = useContour();
  const withActs = hasActs(contour);
  const role = useClientRole();
  const [pay, setPay] = useState(params.get('pay') === '1');
  // Back from the bank's payment page: ask the bank, not the URL, whether it went through.
  const checkoutId = params.get('checkout');
  const { data: back } = useCheckoutStatus(checkoutId);
  const [pdf, setPdf] = useState<'idle' | 'loading' | 'failed'>('idle');
  const downloadPdf = async () => {
    if (!inv) return;
    setPdf('loading');
    try {
      const url = URL.createObjectURL(await api.invoicePdf(inv.id));
      const a = Object.assign(document.createElement('a'), { href: url, download: `Счёт № ${inv.number}.pdf` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setPdf('idle');
    } catch {
      setPdf('failed');
    }
  };

  if (!inv && invQ.error instanceof ApiError && invQ.error.status === 404)
    return (
      <div className="rounded-lg border border-dashed border-strong px-6 py-10 text-center" role="status">
        <p className="font-medium">Такого счёта нет</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted">Возможно, ссылка устарела или счёт принадлежит другой компании. Все ваши счета собраны в разделе «Деньги».</p>
        <Link to="/money" className="btn-secondary mt-4 inline-flex">
          К разделу «Деньги»
        </Link>
      </div>
    );
  if (!inv) return <QueryPending query={invQ} />;
  const s = invoiceStatus[inv.status];
  const act = acts?.find((a) => a.invoiceId === inv.id || a.invoiceIds?.includes(inv.id));
  const cancelled = inv.status === 'cancelled';
  const payable = inv.status === 'sent' || inv.status === 'overdue';

  return (
    <>
      <Link to="/money" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Деньги
      </Link>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px]">
        <article className="min-w-0">
          <header className="mb-8">
            <h1 className="text-xl font-semibold tracking-tight">Счёт № {inv.number}</h1>
            <p className="mt-1 text-muted">
              от {formatDate(inv.issuedAt)}
              {inv.basis ? `, ${inv.basis}` : ''}
              {inv.projectTitle && inv.projectId && (
                <>
                  , проект{' '}
                  {clientCan(role, 'work') ? (
                    <Link to={`/p/${inv.projectId}`} className="underline decoration-strong underline-offset-2 hover:decoration-ink">
                      {inv.projectTitle}
                    </Link>
                  ) : (
                    inv.projectTitle
                  )}
                </>
              )}
            </p>
            {inv.payer?.name && <p className="mt-1 text-sm text-muted">Плательщик: {inv.payer.name}</p>}
          </header>

          <div className="overflow-x-auto"><table className="w-full text-left sm:min-w-[520px]">
            <thead className="text-sm text-muted">
              <tr className="border-b border-line">
                <th className="py-2 pr-4 font-normal">Услуга</th>
                <th className="hidden py-2 pr-4 text-right font-normal sm:table-cell">Кол-во</th>
                <th className="py-2 text-right font-normal">Сумма</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {inv.lines.map((l, i) => (
                <tr key={i}>
                  <td className="py-3 pr-4">
                    <div className="font-medium">{l.title}</div>
                    {l.note && <div className="text-sm text-muted">{l.note}</div>}
                    <div className="num text-sm text-muted sm:hidden">
                      {formatQuantity(l.quantity)}
                      {l.unit ? ` ${l.unit}` : ''}
                    </div>
                  </td>
                  <td className="num hidden py-3 pr-4 text-right sm:table-cell">
                    {formatQuantity(l.quantity)}
                    {l.unit ? ` ${l.unit}` : ''}
                  </td>
                  <td className="num py-3 text-right">{formatMoney(l.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-strong">
                <td className="pt-4 text-muted">
                  {contour?.legal.totalLabel ?? 'Итого'}
                </td>
                <td className="hidden sm:table-cell" />
                <td className="num pt-4 text-right text-lg font-semibold">{formatMoney(inv.total)}</td>
              </tr>
            </tfoot>
          </table></div>
          {contour?.legal.invoiceNote && <p className="mt-3 text-sm text-muted">{contour.legal.invoiceNote}</p>}
          {inv.comment && <p className="mt-4 whitespace-pre-line text-sm">{inv.comment}</p>}

          <button className="btn-secondary mt-8" onClick={downloadPdf} disabled={pdf === 'loading'}>
            {pdf === 'loading' ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />} Скачать счёт в PDF
          </button>
          {pdf === 'failed' && <p className="mt-2 text-sm text-overdue">Не удалось получить PDF. Попробуйте через минуту.</p>}
        </article>

        <aside className="lg:sticky lg:top-[80px] lg:self-start">
          {checkoutId && <CheckoutBanner status={back?.checkout.status} who={providerOf(contour).who} onRetry={() => setPay(true)} />}
          <div className="frame px-6 py-6">
            <Status tone={s.tone}>{s.label}</Status>
            <div className="num mt-2 text-xl font-semibold tracking-tight">{formatMoney(inv.total)}</div>
            {cancelled && <p className="mt-3 text-sm text-muted">Счёт отменён студией, оплачивать его не нужно.</p>}
            <p className="mt-1 text-sm text-muted">
              {inv.status === 'paid' && inv.payment && `Оплачен ${formatDate(inv.payment.at)} ${methodLabel[inv.payment.method]}`}
              {payable && `Оплатить до ${formatDate(inv.dueAt)}, ${formatDue(inv.dueAt)}`}
              {inv.status === 'processing' && 'Вы сообщили о переводе. Ждём поступления по выписке.'}
            </p>
            {payable && (
              <button className="btn-primary mt-5 h-10 w-full" onClick={() => setPay(true)}>
                Оплатить
              </button>
            )}
            {inv.status === 'processing' && (
              <p className="mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="size-4 animate-spin" /> Проверяем оплату
              </p>
            )}
          </div>
          {!cancelled && <div className="mt-6">
            <Meta
              items={[
                { label: 'Оплачено', value: <span className="num">{formatMoney(inv.paid)}</span> },
                ...(inv.payment ? [{ label: 'Платёж', value: inv.payment.reference }] : []),
                ...(inv.payment?.receipt ? [{ label: withActs ? 'Чек' : 'Квитанция', value: inv.payment.receipt }] : []),
                ...(withActs
                  ? [
                      {
                        label: 'Акт',
                        value: act ? (
                          <Link to="/money?tab=acts" className="hover:underline">
                            Закрыт актом № {act.number}, {actStatus[act.status].label.toLowerCase()}
                          </Link>
                        ) : (
                          'студия пришлёт на подпись'
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </div>}
        </aside>
      </div>

      <PayDialog
        invoice={inv}
        open={pay}
        onClose={() => {
          setPay(false);
          if (params.get('pay')) setParams({});
        }}
      />
    </>
  );
}

function CheckoutBanner({ status, who, onRetry }: { status?: string; who: string; onRetry: () => void }) {
  if (!status || status === 'pending')
    return (
      <p className="mb-4 flex items-center gap-2 text-sm" role="status">
        <Loader2 className="size-4 animate-spin" /> {who} подтверждает платёж…
      </p>
    );
  if (status === 'paid')
    return (
      <p className="mb-4 text-sm" role="status">
        Платёж зачислен, счёт оплачен.
      </p>
    );
  if (status === 'mismatch')
    return (
      <p className="mb-4 text-sm text-overdue" role="status">
        Сумма платежа {who === 'Банк' ? 'в банке' : `в ${who}`} не совпала со счётом. Менеджер разберётся и напишет вам.
      </p>
    );
  return (
    <p className="mb-4 text-sm" role="status">
      Оплата не завершилась.{' '}
      <button className="underline underline-offset-2" onClick={onRetry}>
        Попробовать ещё раз
      </button>
    </p>
  );
}
